import { Component, forwardRef, inject, Input } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatFormFieldAppearance,
  MatLabel,
  MatFormField,
  MatError,
} from '@angular/material/form-field';
import { CurrencyService, currencySigns, TranslatePipe } from '@speedy/core';
import { noop } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';

@Component({
  selector: 'app-speedy-select',
  templateUrl: './speedy-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SpeedySelectComponent),
    },
  ],
  standalone: true,
  imports: [
    MatLabel,
    MatFormField,
    MatSelect,
    ReactiveFormsModule,
    MatOption,
    MatError,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class SpeedySelectComponent implements ControlValueAccessor {
  private readonly currencyService = inject(CurrencyService);

  /** Flag to set options for select */
  @Input() options!: Array<any>;

  /** Flag to set disabled state */
  @Input() set disabled(value: boolean) {
    if (value) {
      this.control.disable();
    }
  }

  /** Flag to is dropdown has multiple selection */
  @Input() multiple = false;

  /** Flag to set label */
  @Input() label!: string;

  /** Flag to set placeholder */
  @Input() placeholder!: string;

  /** Flag to set field appearance */
  @Input() appearance: MatFormFieldAppearance = 'outline';

  /** Flag to set field font size */
  @Input() fontSize = 14;

  /** Flag to set is field required */
  @Input() required = true;

  /** Flag to set is field required */
  @Input() control: FormControl;

  /** Flag to set the value what we will use to show the name in the list of options */
  @Input() optionValueName!: string;

  /** Flag to toggle showing currency at the end of dropdown options names */
  @Input() showCurrency = false;

  touched = false;

  currentCurrency$ = this.currencyService.currentCurrency$;

  writeValue(value: string | number) {
    this.control.setValue(value, { emitEvent: false });
  }

  public onTouched: () => void = noop;
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private onChange: (_: any) => void = noop;
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public propagateChange: (_: any) => void = noop;

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  onInputChange(value: string | number) {
    this.onChange(value);
  }

  getOptionValue(option: any): string {
    if (option.id) {
      return option.id;
    } else {
      return option.value;
    }
  }

  getOptionName(option: { [key: string]: string }, currency: string): string {
    const result = option[this.optionValueName] || option.name || option.label || '';
    return this.showCurrency ? result + (currencySigns[currency] || currency) : result;
  }
}
